import React, {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {Button, MenuItem} from '@mui/material';
import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import styled from 'styled-components';
import {DataEntry, Label} from 'app/components/match/liveResult/CommonRowStyles';
import {LoadingButton} from '@mui/lab';
import {Controller, FieldError, useForm, UseFormSetError} from 'react-hook-form';
import {isNumber} from 'app/helpers/ValidationRules';
import {CustomNumberTextField, CustomTextField} from 'app/components/match/liveResult/CustomNumberTextField';
import PlayerSelect from 'app/components/match/liveResult/updateScorer/PlayerSelect';
import {
  InitScorerFormData,
  Inputs,
  MatchPlayersResponse,
  Player,
  StoreGoalEventData,
} from 'app/components/match/liveResult/updateScorer/sharedTypes';
import {goalType} from 'app/helpers/goalType.const';
import DeleteScorerButton from 'app/components/match/liveResult/updateScorer/DeleteScorerButton';
import {useStoreSofttickerFormDataInSessionStorage} from 'app/hooks/useStoreSofttickerFormDataInSessionStorage';

const FormContainer = styled.form`
  display: grid;
  gap: ${SIZES['16']};
  grid-template-columns: repeat(3, auto);
  grid-template-rows: 1fr repeat(4, 64px) 20px auto;
  height: 30rem;
`;

const GridRow = styled.div`
  grid-column: span 3;
  display: grid;
  grid-template-columns: repeat(2, 1fr) 40px;
  column-gap: ${SIZES['16']};
`;

const GridElement = styled.div`
  width: 100%;
  grid-column: span 2;
`;

const SharedErrorContainer = styled.div`
  grid-column: span 3;
  min-height: ${SIZES['20']};
  display: flex;
  flex-direction: column;
`;

const ErrorTypography = styled(Typography)`
  &&& {
    color: ${COLORS.red};
  }
`;

const ActionContainer = styled.div`
  padding-top: ${SIZES['8']};
  grid-column: span 3;
  display: flex;
  justify-content: space-between;
`;

const getOtherTeam = (teams: MatchPlayersResponse, teamSlug: string): Player[] => {
  const slug = Object.keys(teams).find(key => key !== teamSlug);
  if (!slug) {
    return [];
  }
  return teams[slug];
};

const hasGoals = (goals: number | string | null): boolean => {
  if (typeof goals === 'number') {
    return goals > 0;
  }
  if (typeof goals === 'string') {
    const number = parseInt(goals, 10);
    return number > 0;
  }
  return false;
};

const defaultInitData: Inputs = {
  homeGoals: null,
  awayGoals: null,
  minute: null,
  category: 'goal_shoot',
  team: '',
  scorer: '',
  assistant: '',
};

interface UpdateScorerFormProps {
  match: any;
  onClose: () => void;
  onDelete?: (matchEventId: number) => void;
  initData: InitScorerFormData | null;
  matchEventId: number | null;
  players: MatchPlayersResponse | null;
  onSubmit: (data: StoreGoalEventData, setFieldError: UseFormSetError<Inputs>) => void;
  submissionLoading: boolean;
  submissionError: string | null;
}

export default function UpdateScorerForm({
  onClose,
  match,
  onDelete,
  initData,
  matchEventId,
  players,
  onSubmit,
  submissionLoading,
  submissionError,
}: UpdateScorerFormProps) {
  const [deletionError, setDeletionError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: {errors, isDirty},
    setValue,
    setError,
    watch,
    control,
    getValues,
  } = useForm<Inputs>({
    defaultValues: {
      homeGoals: initData?.homeGoals ?? defaultInitData.homeGoals,
      awayGoals: initData?.awayGoals ?? defaultInitData.awayGoals,
      minute: initData?.minute ?? defaultInitData.minute,
      category: initData?.category ?? defaultInitData.category,
      team: initData?.team ?? defaultInitData.team,
      scorer: initData?.scorer ?? defaultInitData.scorer,
      assistant: initData?.assistant ?? defaultInitData.assistant,
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const getSharedError = useCallback(
    (errorObjects: (FieldError | undefined)[]): ReactElement | null => {
      const firstError = errorObjects.find(error => error);
      return firstError ? <ErrorTypography variant='caption1'>{firstError.message}</ErrorTypography> : null;
    },
    [errors]
  );

  const initializeFormData = useCallback(
    (data: Inputs) => {
      Object.keys(data).forEach(key => {
        setValue(key as keyof Inputs, data[key]);
      });
    },
    [setValue]
  );

  useStoreSofttickerFormDataInSessionStorage<Inputs>('scorerFormData', getValues, initializeFormData);

  const team = watch('team');
  const category = watch('category');
  const homeGoals = watch('homeGoals');
  const awayGoals = watch('awayGoals');
  const isOwnGoal = useMemo(() => category === 'goal_own_goal', [category]);
  const scorer = watch('scorer');

  useEffect(() => {
    if (initData?.team) {
      return;
    }
    if (homeGoals === null || homeGoals === undefined || awayGoals === null || awayGoals === undefined) {
      return;
    }
    const homeTeamHasGoals = hasGoals(homeGoals);
    const awayTeamHasGoals = hasGoals(awayGoals);
    if (homeTeamHasGoals && !awayTeamHasGoals) {
      return setValue('team', 'homeTeam', {shouldValidate: true});
    }
    if (!homeTeamHasGoals && awayTeamHasGoals) {
      return setValue('team', 'awayTeam', {shouldValidate: true});
    }
    setValue('team', '');
  }, [initData, homeGoals, awayGoals]);

  const relevantPlayers = useMemo(() => {
    if (!players || !team) {
      return [];
    }
    const teamSlug = match[team].slug;
    return isOwnGoal ? getOtherTeam(players, teamSlug) : players[teamSlug];
  }, [isOwnGoal, players, team, match]);

  const relevantAssistants = useMemo(() => {
    if (!players || !team) {
      return [];
    }
    const teamSlug = match[team].slug;
    return players[teamSlug];
  }, [match, team, players]);

  useEffect(() => {
    if (isDirty) {
      setValue('scorer', '');
    }
  }, [relevantPlayers]);

  useEffect(() => {
    if (isDirty) {
      setValue('assistant', '');
    }
  }, [relevantAssistants]);

  const onValidationSuccess = async ({category, minute, homeGoals, awayGoals, team, scorer, assistant}) => {
    const data = {
      minute,
      homeGoals,
      awayGoals,
      teamId: match[team].id,
      scorerId: typeof scorer === 'string' ? null : scorer,
      assistantId: typeof assistant === 'string' ? null : assistant,
      type: category,
    };
    onSubmit(data, setError);
  };

  return (
    <FormContainer>
      <GridRow>
        <CustomNumberTextField
          error={!!errors.minute}
          type='number'
          variant='outlined'
          label='Spielminute'
          size='small'
          inputProps={{inputMode: 'numeric'}}
          {...register('minute', {
            required: 'Gib die Spielminute an.',
            validate: {isNumber},
            max: {value: 199, message: 'Es darf maximal Minute 199 angegeben werden.'},
            min: {value: 0, message: 'Es muss mindestens Minute 0 angegeben sein.'},
          })}
        />
        <DataEntry justify='center'>
          <CustomNumberTextField
            error={!!errors.homeGoals}
            type='number'
            variant='outlined'
            label='Heim'
            size='small'
            maxWidth={52}
            textAlign='center'
            inputProps={{inputMode: 'numeric'}}
            InputLabelProps={{
              shrink: true,
            }}
            {...register('homeGoals', {
              required: 'Gib die Anzahl der Heimtore an.',
              validate: {isNumber},
              max: {
                value: initData?.homeGoals ?? match.homeGoal,
                message: 'Eingabe darf die Anzahl der Heimtore nicht übersteigen.',
              },
              min: {value: 0, message: 'Die Anzahl der Heimtore muss mindestens 0 sein.'},
            })}
          />
          <Label variant='body1' padding={8}>
            :
          </Label>
          <CustomNumberTextField
            error={!!errors.awayGoals}
            type='number'
            variant='outlined'
            label='Gast'
            size='small'
            maxWidth={52}
            textAlign='center'
            inputProps={{inputMode: 'numeric'}}
            InputLabelProps={{
              shrink: true,
            }}
            {...register('awayGoals', {
              required: 'Gib die Anzahl der Gasttore an.',
              validate: {isNumber},
              max: {
                value: initData?.awayGoals ?? match.awayGoal,
                message: 'Eingabe darf die Anzahl der Gasttore nicht übersteigen.',
              },
              min: {value: 0, message: 'Die Anzahl der Gasttore muss mindestens 0 sein.'},
            })}
          />
        </DataEntry>
        {matchEventId && onDelete ? (
          <DeleteScorerButton
            matchEventId={matchEventId}
            matchSlug={match.slug}
            onDelete={onDelete}
            onError={setDeletionError}
          />
        ) : (
          <div />
        )}
        <SharedErrorContainer>
          {getSharedError([errors.minute, errors.homeGoals, errors.awayGoals])}
        </SharedErrorContainer>
      </GridRow>
      <GridElement>
        <CustomTextField
          select
          error={!!errors.category}
          fullWidth={true}
          variant='outlined'
          size='small'
          label='Torkategorie'
          id='category-select'
          defaultValue={initData?.category ?? defaultInitData.category}
          helperText={errors.category?.message ?? ''}
          inputProps={register('category')}>
          {Object.entries(goalType)
            .filter(([slug, _]) => slug !== 'goal_freekick')
            .map(([slug, label]) => (
              <MenuItem key={slug} value={slug}>
                {label || 'Schuss'}
              </MenuItem>
            ))}
        </CustomTextField>
      </GridElement>
      <div />
      <GridElement>
        <Controller
          render={({field}) => (
            <CustomTextField
              select
              error={!!errors.team}
              fullWidth={true}
              variant='outlined'
              size='small'
              label='Tor für'
              id='team-select'
              helperText={errors.team?.message ?? ''}
              {...field}>
              <MenuItem disabled={!hasGoals(homeGoals)} value='homeTeam'>
                {match.homeTeamName}
              </MenuItem>
              <MenuItem disabled={!hasGoals(awayGoals)} value='awayTeam'>
                {match.awayTeamName}
              </MenuItem>
            </CustomTextField>
          )}
          control={control}
          rules={{
            required: 'Gib das Team an, das dieses Tor erzielt hat.',
          }}
          name='team'
          defaultValue={initData?.team ?? defaultInitData.team}
        />
      </GridElement>
      <div />
      <PlayerSelect
        error={errors.scorer}
        defaultValue={initData?.scorer ?? defaultInitData.scorer}
        control={control}
        label='Torschütze'
        name='scorer'
        players={relevantPlayers}
        show={!!(initData?.scorer || team)}
      />
      <div />
      <PlayerSelect
        error={errors.assistant}
        defaultValue={initData?.assistant ?? defaultInitData.assistant}
        control={control}
        label='Vorlagengeber'
        name='assistant'
        players={relevantAssistants}
        show={!!((initData?.scorer || scorer) && !isOwnGoal)}
      />
      <div />
      <SharedErrorContainer>
        {submissionError && <ErrorTypography variant='caption1'>{submissionError}</ErrorTypography>}
        {deletionError && <ErrorTypography variant='caption1'>{deletionError}</ErrorTypography>}
      </SharedErrorContainer>
      <ActionContainer>
        <Button variant='text' sx={{color: COLORS.darkestGrey}} onClick={onClose}>
          Abbrechen
        </Button>
        <LoadingButton
          variant='contained'
          color='secondary'
          onClick={handleSubmit(onValidationSuccess)}
          loading={submissionLoading}>
          Speichern
        </LoadingButton>
      </ActionContainer>
    </FormContainer>
  );
}
